import { Box, Grid, Typography } from "@mui/material";
import { Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { Inputs } from "../formInputs";
import * as Yup from "yup";
import { useDispatch, useSelector } from "../../store";
import { debounce } from "lodash";
import { searchJob, setJobFilter } from "../../slices/job";
import { defaultParams } from "../../types/searchParams";
import { Base } from "../base";
import theme from "../../theme";

interface Props {
  handleMilestoneSubmit: (values: { milestone?: Date; title?: string }) => void;
  modal?: boolean;
  handleClose?: () => void;
  start?: Date;
  handleDelete?: () => void;
  handleView?: () => void;
}

const JobMilestoneForm: React.FC<Props> = (props) => {
  const {
    handleMilestoneSubmit,
    modal,
    handleClose,
    start,
    handleDelete,
    handleView,
  } = props;
  const dispatch = useDispatch();
  const { highlights } = useSelector((store) => store.highlight);
  const {
    milestoneStatus,
    jobMilestone,
    jobSearchResults,
    jobFilter,
    searchStatus,
  } = useSelector((store) => store.job);
  const { milestone, status } = useSelector((store) => store.milestone);
  const [deleteMode, setDeleteMode] = useState<boolean>(false);

  const validationSchema = Yup.object().shape({
    start: Yup.date().required("Required").typeError("Required"),
    end: Yup.date().nullable(),
    title: Yup.string().required("Required").typeError("Required"),
  });

  const updateSearch = debounce((query: string) => {
    dispatch(searchJob({ search: { ...defaultParams, query } }));
    dispatch(setJobFilter({ ...defaultParams, query }));
  }, 500);

  const initialValues = !!jobMilestone.id
    ? {
        ...jobMilestone,
        start: new Date(jobMilestone?.start!),
        end: new Date(jobMilestone?.end!),
      }
    : {
        ...milestone,
        start: start
          ? start
          : milestone.start
          ? new Date(milestone.start)
          : undefined,
        end: milestone.end ? new Date(milestone.end) : undefined,
        title: milestone.title ? milestone.title : "",
        jobType: undefined,
      };

  const renderOption = (
    props: React.HtmlHTMLAttributes<HTMLLIElement>,
    option: any
  ) => {
    return (
      <li
        {...props}
        key={option.value}
        style={{ backgroundColor: theme.palette.background.paper }}
      >
        <Typography
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              height: "25px",
              width: "25px",
              backgroundColor: option.color,
            }}
          />
          {option.name}
        </Typography>
      </li>
    );
  };

  return (
    <>
      {!deleteMode ? (
        <Formik
          initialValues={initialValues}
          onSubmit={(values, { resetForm }) => {
            handleMilestoneSubmit(values);
            resetForm();
          }}
          validationSchema={validationSchema}
        >
          {({ values }) => (
            <Form noValidate>
              <Grid container spacing={2} mt={2}>
                <Grid size={{ sm: modal ? 4 : 2, xs: 12 }}>
                  <Field name="start" label="Start" component={Inputs.Date} />
                </Grid>
                <Grid size={{ sm: modal ? 4 : 2, xs: 12 }}>
                  <Field name="end" label="End" component={Inputs.Date} />
                </Grid>
                <Grid size={{ sm: modal ? 4 : 2, xs: 12 }}>
                  <Field
                    name="calendarHighlight"
                    label="Highlight Type"
                    component={Inputs.Dropdown}
                    options={highlights}
                    staticFilter
                    renderOption={renderOption}
                  />
                </Grid>
                <Grid size={{ sm: modal ? 12 : 6, xs: 12 }}>
                  <Field
                    name="title"
                    label="Title"
                    component={Inputs.Text}
                    required
                  />
                </Grid>
                {/* TODO Add back in when Answertype Dates is fixed */}
                {/* {!!modal && jobMilestone.id === 0 && milestone.id >= 0 && (
                  <Grid size={{ xs: 12 }}>
                    <Field
                      name="jobType"
                      label="Attach to job?"
                      component={Inputs.Dropdown}
                      options={
                        jobSearchResults?.results && jobFilter.query
                          ? jobSearchResults.results.map((j) => {
                              return {
                                id: j.id,
                                value: j.title,
                              };
                            })
                          : []
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        updateSearch(e.currentTarget.value)
                      }
                      noOptionsText={
                        jobFilter.query === ""
                          ? "Start typing to search"
                          : searchStatus === "loading"
                          ? "Loading..."
                          : undefined
                      }
                    />
                  </Grid>
                )} */}
                <Grid size={{ xs: 12 }}>
                  <Grid
                    container
                    direction={"row"}
                    justifyContent={modal ? "space-around" : "flex-end"}
                  >
                    <Grid>
                      {modal && handleClose && (
                        <Base.Button
                          text="Cancel"
                          onClick={handleClose}
                          type="secondary"
                        />
                      )}
                    </Grid>
                    <Grid>
                      {(jobMilestone?.id > 0 || milestone.id > 0) &&
                        modal &&
                        handleDelete && (
                          <Base.Button
                            text="Delete"
                            onClick={() => setDeleteMode(true)}
                            type="delete"
                          />
                        )}
                    </Grid>
                    {jobMilestone?.id > 0 && modal && handleView && (
                      <Grid>
                        <Base.Button
                          text="View Job"
                          onClick={handleView}
                          type="active"
                        />
                      </Grid>
                    )}
                    <Grid>
                      <Inputs.Submit
                        text={
                          milestone.id > 0 || jobMilestone.id > 0
                            ? "Save"
                            : "Add Date"
                        }
                        isSubmitting={
                          milestoneStatus === "loading" || status === "loading"
                        }
                        disabled={
                          new Date(values.start!) > new Date(values.end!)
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      ) : (
        <Grid size={{ xs: 12 }}>
          <Box sx={{ padding: "10px" }}>
            <Base.Text
              text={`Are you sure you want to delete ${
                milestone.title ? milestone.title : jobMilestone.title
              }`}
              variant="h3"
              center
            />
          </Box>
          <Grid container direction={"row"} justifyContent={"space-around"}>
            {modal && handleClose && (
              <Grid>
                <Base.Button
                  text="Cancel"
                  onClick={() => {
                    handleClose();
                    setDeleteMode(false);
                  }}
                  type="secondary"
                />
              </Grid>
            )}
            <Base.Button text="Delete" onClick={handleDelete} type="delete" />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default JobMilestoneForm;
